import { fetchCsvExport } from '@/lib/utils/exports'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import {
  INDETERMINATE,
  PRODUCT_CATEGORIES,
  VOL_CORRECTED_FORECAST_STATUSES,
  VOLUME_FORECAST_STATUS_FILTER_VALUES,
  YEAR_FLAG_VALUES,
} from '@repo/constants'
import { monthIndex } from '@repo/zod-schemas'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

export const volumeForecastsSearchSchema = z.object({
  crtPeriod: z.string().optional(),
  month: z.union([z.literal(INDETERMINATE), monthIndex]).optional(),
  year: z.enum(YEAR_FLAG_VALUES).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  brand: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  packContentSize: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  marking: z.array(z.string()).optional(),
  keyword: z.string().optional(),
  status: z.array(z.enum(VOLUME_FORECAST_STATUS_FILTER_VALUES)).optional(),
  mode: z.enum(['material', 'fmc', 'vapour', 'nrt', 'materialGroup']).optional(),
  page: z.number().optional(),
  pageSize: z.number().optional(),
  sort: z
    .object({
      id: z.string(),
      desc: z.boolean(),
    })
    .optional(),
})

export type VolumeForecastsSearch = z.infer<typeof volumeForecastsSearchSchema>

/**
 * Strip the `INDETERMINATE` sentinel from `month` so the backend receives no
 * `month` query and falls back to its "all twelve months" branch.
 */
function toWireQuery(search: VolumeForecastsSearch) {
  const { month, ...rest } = search
  return month === INDETERMINATE ? rest : { ...rest, month }
}

async function getVolumeForecasts(search: VolumeForecastsSearch) {
  const api = getApi()
  const res = await api.crt['volume-forecasts']['table'].$get({
    query: stringifyValues(toWireQuery(search)),
  })

  return await res.json()
}

/** How often to re-check a period whose corrected volumes are still being computed. */
const IN_PROGRESS_POLL_MS = 5_000

export function useVolumeForecasts(queries: VolumeForecastsSearch) {
  return useQuery({
    queryKey: ['volumeForecasts', queries],
    queryFn: () => getVolumeForecasts(queries),
    // A queued or running recompute finishes on the worker, with nothing to push the result back, so
    // the page polls until the snapshot settles either way.
    refetchInterval: ({ state }) => {
      const status = state.data?.cacheStatus?.status
      return status === VOL_CORRECTED_FORECAST_STATUSES.PENDING ||
        status === VOL_CORRECTED_FORECAST_STATUSES.PROCESSING
        ? IN_PROGRESS_POLL_MS
        : false
    },
  })
}

async function getVolumeForecastBreakdown({
  crtPeriod,
  material,
  period,
}: {
  crtPeriod?: string
  material: string
  period: string
}) {
  const api = getApi()
  // Built by hand rather than through `stringifyValues`, which widens every value to `string |
  // undefined` and so loses the two the endpoint requires.
  const res = await api.crt['volume-forecasts']['breakdown'].$get({
    query: { material, period, ...(crtPeriod ? { crtPeriod } : {}) },
  })

  return await res.json()
}

/**
 * The per-cell diagnostics behind each material's corrected volume for one month, keyed by material.
 * Only the final volume is cached, so these are recomputed per request — cheap for a single SKU, and
 * kept off every table response. Fetched one material at a time because the grouped table modes open
 * the dialog on several at once, and each is independently cacheable.
 *
 * Runs only while the dialog that needs it is open.
 */
export function useVolumeForecastBreakdowns(
  params: { crtPeriod?: string; materials: string[]; period: string },
  options: { enabled: boolean },
) {
  const results = useQueries({
    queries: params.materials.map((material) => {
      const query = { crtPeriod: params.crtPeriod, material, period: params.period }
      return {
        queryKey: ['volumeForecastBreakdown', query],
        queryFn: () => getVolumeForecastBreakdown(query),
        enabled: options.enabled,
      }
    }),
  })

  return {
    // `isPending` stays true on a disabled query, so gate on the fetch actually being in flight.
    isLoading: results.some((result) => result.isLoading),
    isError: results.some((result) => result.isError),
    breakdownByMaterial: new Map(
      params.materials.map((material, index) => [material, results[index]?.data?.results ?? []]),
    ),
  }
}

/**
 * Queues a recompute of the period's corrected volumes. Idempotent — the worker coalesces per period —
 * so the page can offer it whenever the cache is unusable.
 */
export function useRecomputeVolumeForecasts() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (crtPeriod: string) => {
      const api = getApi()
      const res = await api.crt['volume-forecasts']['recompute'].$post({ json: { crtPeriod } })
      return await res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['volumeForecasts'] }),
  })
}

async function getVolumeForecastsViewCsv(search: VolumeForecastsSearch, signal?: AbortSignal) {
  const stringified = stringifyValues(toWireQuery(search))
  const filtered = Object.fromEntries(
    Object.entries(stringified).filter(([, value]) => value !== undefined),
  )
  const queryParams = new URLSearchParams(filtered).toString()
  return fetchCsvExport(`/crt/volume-forecasts/export-view?${queryParams}`, signal)
}

export const volumeForecastsExportQueryOptions = (search: VolumeForecastsSearch) => ({
  queryKey: ['volumeForecastsExport', search],
  queryFn: ({ signal }: { signal: AbortSignal }) => getVolumeForecastsViewCsv(search, signal),
})
